import { useState, type FC } from "react";
import Card from "../../atoms/Card";
import Subtitle from "../../atoms/typography/Subtitle";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { theme } from "../../../theme";
import InCardSectionTitle from "../../molecules/InCardSectionTitle";
import BodySmall from "../../atoms/typography/BodySmall";
import styled from "styled-components";
import { WarningIcon } from "../../atoms/icons/WarningIcon";
import { Col, Row } from "react-grid-system";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import { useClientRiskScore } from "../../../graphql/hooks/aml/useClientRiskScore";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import { getAmlStatusBadge } from "../../molecules/ClientCardHeader";
import Alert from "../../molecules/Alert";
import ClientRiskScores from "../../organisms/ClientRiskScores";
import Modal from "../../molecules/Modal";
import EditRiskScoreModalContent from "../../molecules/modals/EditRiskScoreModalContent";
import ClientMonitoringEvents from "../../organisms/ClientMonitoringEvents";
import { useTranslation } from "react-i18next";
import { useTriggerRiskScoreDetermination } from "../../../graphql/hooks/aml/useTriggerRiskScoreDetermination";
import ListTabBar from "../../molecules/ListTabBar";

interface Props {
  clientIdentifier: string;
}

const CenteredDivFlexed = styled.div<{ gap: number }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.gap}px;
`;

const ScoreManuallyEditedContainer = styled.div`
  background-color: ${theme.colors.info05};
  border-radius: 4px;
  padding: 8px;
`;

const TABS = [
  { tabLabelKey: "aml.riskScores", id: "riskScores" },
  { tabLabelKey: "aml.monitoringEvents", id: "monitoringEvents" },
];

const ClientAml: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const [editRiskScoreOpen, setEditRiskScoreOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTab, setSelectedTab] = useState(TABS[0].id);

  const { clientRiskScore, error, loading, refetch } = useClientRiskScore(
    clientIdentifier,
    false,
  );

  const onCompleted = (result?: any) => {
    if (result?.invoice?.triggerRiskScoreDetermination?.success === false) {
      setErrorMessage(t("aml.couldNotFetchAmlForClient"));
    }
  };
  const onError = (e?: any) => {
    setErrorMessage(e?.toString() ?? t("aml.couldNotFetchAmlForClient"));
  };
  const {
    triggerRiskScoreDetermination,
    error: refetchError,
    loading: reftechLoading,
  } = useTriggerRiskScoreDetermination({ onCompleted, onError });

  const onClickRefetchResult = async () => {
    try {
      await triggerRiskScoreDetermination({ variables: { clientIdentifier } });
    } catch (error) {
      console.log(error);
      setErrorMessage(error?.toString() ?? t("aml.couldNotFetchAmlForClient"));
    }
  };

  return (
    <div>
      <HorizontalSpacer spacing={2} />
      <Card spacing={2}>
        <Row align="center">
          <Col lg={2}>
            <Subtitle color={theme.colors.primary}>{t("aml.aml")}</Subtitle>
          </Col>
          <Col lg={3}>
            <InCardSectionTitle
              title={t("aml.riskLevel")}
              status={getAmlStatusBadge(
                clientRiskScore?.riskScore?.current?.totalScore,
                clientRiskScore?.riskScore?.current?.riskScoreClass,
                clientRiskScore?.riskScore?.currentManualRiskScore !== null,
              )}
              onClickEdit={() => setEditRiskScoreOpen(true)}
            />
          </Col>
          <Col lg={3}>
            <InCardSectionTitle
              title={t("aml.events")}
              nodeContent={
                <CenteredDivFlexed gap={4}>
                  <WarningIcon color={theme.colors.error} />
                  <BodySmall fontweight={600}>
                    {clientRiskScore?.monitoringEvents?.items?.length}{" "}
                    {t("aml.eventsIdentified").toLowerCase()}
                  </BodySmall>
                </CenteredDivFlexed>
              }
            />
          </Col>
          <Col lg={4} style={{ display: "flex", justifyContent: "flex-end" }}>
            <OutlinedButton
              title={t("common.refresh")}
              medium
              color={theme.colors.primary}
              icon={<RefreshIcon color={theme.colors.primary} />}
              onClick={onClickRefetchResult}
              loading={loading || reftechLoading}
            />
          </Col>
        </Row>

        {(refetchError || errorMessage) && (
          <Alert
            type="error"
            title={t("aml.couldNotFetchAmlForClient")}
            message={error?.toString() || errorMessage}
          />
        )}

        <HorizontalSpacer spacing={1} />
        {clientRiskScore?.riskScore?.currentManualRiskScore && (
          <ScoreManuallyEditedContainer>
            <BodySmall>{t("aml.currentRiskScoreIsManuallyEdited")}</BodySmall>
            <BodySmall color={theme.colors.textSecondary}>
              {
                clientRiskScore.riskScore.currentManualRiskScore?.comment
                  ?.content
              }
            </BodySmall>
          </ScoreManuallyEditedContainer>
        )}

        <HorizontalSpacer spacing={1} />
        <ListTabBar
          tabs={TABS}
          selectedId={selectedTab}
          onSelectTabId={setSelectedTab}
        />
        <HorizontalSpacer spacing={2} />

        {error && (
          <Alert
            type="error"
            title={t("aml.couldNotFetchAmlForClient")}
            message={error?.toString()}
          />
        )}

        {loading && <LoadingSpinner />}

        {selectedTab === "riskScores" && clientRiskScore?.riskScore && (
          <ClientRiskScores
            riskScore={clientRiskScore?.riskScore}
            clientIdentifer={clientIdentifier}
            refetchRiskScore={refetch}
          />
        )}

        {selectedTab === "monitoringEvents" && (
          <ClientMonitoringEvents clientIdentifier={clientIdentifier} />
        )}
      </Card>

      {editRiskScoreOpen && (
        <Modal closeIcon onClose={() => setEditRiskScoreOpen(false)}>
          <EditRiskScoreModalContent
            clientIdentifier={clientIdentifier}
            riskScore={clientRiskScore?.riskScore?.current?.totalScore ?? 0}
            manualRiskScore={
              clientRiskScore?.riskScore?.current?.manualRiskScore
            }
            currentManualRiskScore={
              clientRiskScore?.riskScore?.currentManualRiskScore
            }
            onClose={() => setEditRiskScoreOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ClientAml;
